import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useGetProjectsQuery } from '../../store/api/workspaceApi';
import WorkspaceChatPanel from './WorkspaceChatPanel';
import EmptyState from './EmptyState';
import ModeSelector from './ModeSelector';
import ExploreCanvas from './canvases/ExploreCanvas';
import AnalyzeCanvas from './canvases/AnalyzeCanvas';
import BuildCanvas from './canvases/BuildCanvas';
import AskContextCanvas from './canvases/AskContextCanvas';
import type { WorkspaceTab } from '../../hooks/useWorkspace';

/* ---------- styled ---------- */

const Page = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 1.25rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-surface);
  flex-shrink: 0;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const ProjectSelect = styled.select`
  padding: 0.4375rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--input-bg);
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-primary);
  outline: none;
  cursor: pointer;
  min-width: 160px;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 2px;
  background: var(--bg-elevated);
  border-radius: 8px;
  padding: 2px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  padding: 0.375rem 1rem;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'var(--bg-surface)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text-primary)' : 'var(--text-muted)')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};

  &:hover {
    color: var(--text-primary);
  }
`;

const NewSessionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4375rem 0.875rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--accent);
    color: white;
    border-color: var(--accent);
  }
`;

const Main = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: hidden;
`;

const ChatColumn = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}%;
  min-width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const ResizeHandle = styled.div`
  width: 4px;
  cursor: col-resize;
  background: transparent;
  flex-shrink: 0;
  transition: background 0.15s;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 1px;
    width: 2px;
    background: var(--border);
  }

  &:hover::after,
  &:active::after {
    background: var(--accent);
  }
`;

const CanvasColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* ---------- constants ---------- */

const TABS: { key: WorkspaceTab; label: string }[] = [
  { key: 'explore', label: 'Explore' },
  { key: 'analyze', label: 'Analyze' },
  { key: 'build', label: 'Build' },
];

const DEFAULT_CHAT_WIDTH = 35;
const MIN_CHAT_WIDTH = 20;
const MAX_CHAT_WIDTH = 60;

/* ---------- component ---------- */

const WorkspaceScreen: React.FC = () => {
  const { data: projects = [] } = useGetProjectsQuery();
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH);

  const {
    activeTab,
    setActiveTab,
    interactionMode,
    setInteractionMode,
    resolvedMode,
    effectiveMode,
    currentPlan,
    currentArtefact,
    contextSize,
    turns,
    pendingInputRequest,
    systemNotifications,
    messages,
    isProcessing,
    hasActiveTurn,
    isSending,
    canvasHtml,
    canvasUrl,
    sendMessage,
    submitInputResponse,
    stopSession,
    resetSession,
  } = useWorkspace();

  const isAskMode = effectiveMode === 'ask';

  const handleSend = useCallback(
    (query: string) => {
      sendMessage(query, undefined, selectedProjectId || undefined);
    },
    [sendMessage, selectedProjectId],
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = chatWidth;
      const container = (e.target as HTMLElement).parentElement;
      if (!container) return;
      const containerWidth = container.getBoundingClientRect().width;

      const onMove = (ev: MouseEvent) => {
        const delta = ev.clientX - startX;
        const newPct = startWidth + (delta / containerWidth) * 100;
        setChatWidth(Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, newPct)));
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [chatWidth],
  );

  const hasSession = turns.length > 0;

  const visibleTabs = TABS.filter((t) => {
    if (isAskMode) return true;
    if (effectiveMode === 'explore') {
      return t.key === 'explore' || (t.key === 'build' && canvasHtml.build);
    }
    if (effectiveMode === 'build') {
      return t.key !== 'explore' || canvasHtml.explore;
    }
    return true;
  });

  return (
    <Page>
      {!hasSession ? (
        <EmptyState
          onSend={handleSend}
          modeSelector={
            <ModeSelector
              interactionMode={interactionMode}
              resolvedMode={resolvedMode}
              onChange={setInteractionMode}
            />
          }
        />
      ) : (
        <>
          <Toolbar>
            <ToolbarLeft>
              <ProjectSelect
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="">All projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </ProjectSelect>
            </ToolbarLeft>

            <ToolbarRight>
              {visibleTabs.length > 0 && (
                <TabGroup>
                  {visibleTabs.map((t) => (
                    <TabBtn
                      key={t.key}
                      $active={!isAskMode && activeTab === t.key}
                      onClick={() => setActiveTab(t.key)}
                    >
                      {t.label}
                    </TabBtn>
                  ))}
                </TabGroup>
              )}
              <NewSessionBtn onClick={resetSession}>
                &#x2795; New Session
              </NewSessionBtn>
            </ToolbarRight>
          </Toolbar>

          <Main>
            <ChatColumn $width={chatWidth}>
              <WorkspaceChatPanel
                turns={turns}
                pendingInputRequest={pendingInputRequest}
                systemNotifications={systemNotifications}
                contextSize={contextSize}
                isProcessing={isProcessing}
                hasActiveTurn={hasActiveTurn}
                isSending={isSending}
                activeTab={activeTab}
                onSend={handleSend}
                onSubmitInputResponse={submitInputResponse}
                onStop={stopSession}
                modeSelector={
                  <ModeSelector
                    interactionMode={interactionMode}
                    resolvedMode={resolvedMode}
                    onChange={setInteractionMode}
                  />
                }
              />
            </ChatColumn>

            <ResizeHandle onMouseDown={handleResizeStart} />

            <CanvasColumn>
              {isAskMode ? (
                <AskContextCanvas />
              ) : (
                <>
                  {activeTab === 'explore' && (
                    <ExploreCanvas
                      plan={currentPlan}
                      html={canvasHtml.explore}
                      url={canvasUrl.explore}
                      isProcessing={isProcessing}
                      hasMessages={messages.length > 0}
                    />
                  )}
                  {activeTab === 'analyze' && (
                    <AnalyzeCanvas
                      html={canvasHtml.analyze}
                      url={canvasUrl.analyze}
                      isProcessing={isProcessing}
                      hasMessages={messages.length > 0}
                    />
                  )}
                  {activeTab === 'build' && (
                    <BuildCanvas
                      artefact={currentArtefact}
                      html={canvasHtml.build}
                      url={canvasUrl.build}
                    />
                  )}
                </>
              )}
            </CanvasColumn>
          </Main>
        </>
      )}
    </Page>
  );
};

export default WorkspaceScreen;
